import { useRouter, type ErrorComponentProps } from '@tanstack/react-router'
import { TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiError } from '@/shared/api/api-error'
import { useApiErrorMessage } from '@/shared/i18n/use-api-error-message'
import { Button } from '@/shared/ui/button'
import { EmptyState } from '@/shared/ui/empty-state'

/**
 * Fronteira de erro de ROTA: pega o que explode no loader ou no render de uma
 * página (API fora do ar, 500, bug de render) e mostra uma tela com saída.
 *
 * Antes disto não havia nenhuma: um loader que rejeitasse levava a app para o
 * fallback do router — sem contexto e sem como tentar de novo.
 *
 * `reset` limpa a fronteira; `router.invalidate()` refaz os loaders. Os dois
 * juntos são o que torna o "Tentar novamente" de fato efetivo — só resetar
 * re-renderizaria com o mesmo loader falho em cache.
 */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const { t } = useTranslation('common')
  const router = useRouter()
  const mensagemDeErro = useApiErrorMessage()

  // Erro da API tem código canônico e mensagem traduzida; o resto é bug de
  // render — aí a mensagem crua ajuda mais do que um texto genérico.
  const descricao = error instanceof ApiError ? mensagemDeErro(error) : error.message

  return (
    <EmptyState
      icon={TriangleAlert}
      title={t('states.error')}
      description={descricao}
      action={
        <Button
          variant="primary"
          onClick={() => {
            reset()
            void router.invalidate()
          }}
        >
          {t('actions.retry')}
        </Button>
      }
    />
  )
}
